import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { FiShoppingCart, FiUser, FiMenu, FiX, FiSearch, FiBell } from 'react-icons/fi';
import { categoriesAPI } from '../utils/api';
import './Navbar.css';

const Navbar = () => {
  const { user, isAuthenticated } = useAuth();
  const { getCartCount } = useCart();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryDropdown, setCategoryDropdown] = useState(false);
  const [categories, setCategories] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (categoryDropdown && !event.target.closest('.navbar-category-dropdown')) {
        setCategoryDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [categoryDropdown]);

  const loadCategories = async () => {
    try {
      const response = await categoriesAPI.getAll();
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      const query = searchQuery.trim();
      setSearchQuery('');
      setMobileMenuOpen(false);
      navigate(`/products?search=${encodeURIComponent(query)}`);
    }
  };

  return (
    <nav className="navbar">
      <div className="navbar-container">
        {/* Top Bar */}
        <div className="navbar-top">
          <button 
            className="menu-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
          </button>

          <Link to="/" className="navbar-logo">
            <h1>ShofexBD</h1>
          </Link>

          <div className="navbar-actions">
            <Link to="/blogs" className="nav-action-link">Blogs</Link>
            <Link to="/faqs" className="nav-action-link">FAQs</Link>
            <Link 
              to={isAuthenticated ? "/orders" : "/login"} 
              className="nav-icon-btn" 
              aria-label="Orders"
            >
              <FiBell size={20} />
            </Link>
            <Link to="/cart" className="nav-icon-btn cart-btn" aria-label="Cart">
              <FiShoppingCart size={20} />
              {getCartCount() > 0 && (
                <span className="cart-count">{getCartCount()}</span>
              )}
            </Link>
            <Link 
              to={isAuthenticated ? "/profile" : "/login"} 
              className="nav-icon-btn"
              aria-label="Account"
            >
              <FiUser size={20} />
            </Link>
          </div>
        </div>

        {/* Bottom Bar with Category and Search */}
        <div className="navbar-bottom">
          <div className="navbar-category-dropdown">
            <button 
              className="category-btn"
              onClick={() => setCategoryDropdown(!categoryDropdown)}
            >
              Categories <span className="dropdown-arrow">{categoryDropdown ? '▲' : '▼'}</span>
            </button>
            {categoryDropdown && (
              <div className="category-menu">
                <Link to="/products" onClick={() => setCategoryDropdown(false)}>All Products</Link>
                {categories.slice(0, 10).map(cat => (
                  <Link 
                    key={cat._id} 
                    to={`/products?category=${cat.slug}`} 
                    onClick={() => setCategoryDropdown(false)}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <div className="navbar-categories">
            <Link to="/products" className="category-link">All Products</Link>
            {categories.slice(0, 3).map(cat => (
              <Link 
                key={cat._id} 
                to={`/products?category=${cat.slug}`} 
                className="category-link"
              >
                {cat.name}
              </Link>
            ))}
          </div>

          <form className="navbar-search" onSubmit={handleSearch}>
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            <button type="submit" className="search-btn" aria-label="Search">
              <FiSearch size={18} />
            </button>
          </form>

          <div className="navbar-filter-buttons">
            {categories.slice(3, 5).map(cat => (
              <Link 
                key={cat._id} 
                to={`/products?category=${cat.slug}`} 
                className="filter-btn"
              >
                {cat.name}
              </Link>
            ))}
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="mobile-menu">
            <div className="mobile-search">
              <form onSubmit={handleSearch}>
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <button type="submit"><FiSearch size={20} /></button>
              </form>
            </div>
            <div className="mobile-links">
              <Link to="/" onClick={() => setMobileMenuOpen(false)}>Home</Link>
              <Link to="/products" onClick={() => setMobileMenuOpen(false)}>All Products</Link>
              {categories.slice(0, 8).map(cat => (
                <Link 
                  key={cat._id} 
                  to={`/products?category=${cat.slug}`} 
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {cat.name}
                </Link>
              ))}
              <Link to="/blogs" onClick={() => setMobileMenuOpen(false)}>Blogs</Link>
              <Link to="/faqs" onClick={() => setMobileMenuOpen(false)}>FAQs</Link>
              {isAuthenticated ? (
                <>
                  <Link to="/profile" onClick={() => setMobileMenuOpen(false)}>Profile</Link>
                  <Link to="/orders" onClick={() => setMobileMenuOpen(false)}>My Orders</Link>
                </>
              ) : (
                <Link to="/login" onClick={() => setMobileMenuOpen(false)}>Login</Link>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
